/**
 * The delay ledger: one tick-keyed input book per participant.
 *
 * Deliberately dumb and deliberately strict. It knows nothing about ticks
 * being "late" or a peer being "ahead" — it only answers *do I have every
 * player's stick for tick N yet*, which is the single question input-delay
 * lockstep turns on.
 *
 * **First write wins.** A DataChannel can redeliver, and this protocol sends
 * each filing several times on purpose (`INPUT_REDUNDANCY`), so the same tick
 * arrives repeatedly by design. Overwriting would be the one way a duplicate
 * could change the simulation: two frames disagreeing about tick N, the later
 * one landing after the sender already stepped on the earlier. Ignoring the
 * second copy makes redelivery, reordering and duplication all free.
 */

import type { FlightInputs } from "@red-opus/engine";

export class InputLedger {
  private readonly books: Map<string, Map<number, FlightInputs>>;

  constructor(private readonly ids: readonly string[]) {
    this.books = new Map(ids.map((id) => [id, new Map<number, FlightInputs>()]));
  }

  private bookFor(id: string): Map<number, FlightInputs> {
    const book = this.books.get(id);
    if (!book) throw new Error(`input ledger has no book for "${id}"`);
    return book;
  }

  /** Files one player's stick for one tick. Returns false when that slot was already filled. */
  record(id: string, tick: number, inputs: FlightInputs): boolean {
    const book = this.bookFor(id);
    if (book.has(tick)) return false;
    book.set(tick, inputs);
    return true;
  }

  has(id: string, tick: number): boolean {
    return this.bookFor(id).has(tick);
  }

  /** Every player whose stick for `tick` is still missing, in roster order. */
  missing(tick: number): readonly string[] {
    return this.ids.filter((id) => !this.bookFor(id).has(tick));
  }

  ready(tick: number): boolean {
    return this.missing(tick).length === 0;
  }

  /** The whole roster's inputs for `tick`, in the shape `stepEncounter` takes. */
  take(tick: number): Readonly<Record<string, FlightInputs>> {
    const inputs: Record<string, FlightInputs> = {};
    for (const id of this.ids) {
      const filed = this.bookFor(id).get(tick);
      if (!filed) throw new Error(`input ledger is not ready for tick ${tick}: waiting on ${this.missing(tick).join(", ")}`);
      inputs[id] = filed;
    }
    return inputs;
  }

  /** Highest tick filed for `id`, or 0 when its book is empty. */
  highest(id: string): number {
    let highest = 0;
    for (const tick of this.bookFor(id).keys()) {
      if (tick > highest) highest = tick;
    }
    return highest;
  }

  /** Drops everything strictly before `tick`. Simulated ticks are never revisited. */
  prune(tick: number): void {
    for (const book of this.books.values()) {
      for (const filed of book.keys()) {
        if (filed < tick) book.delete(filed);
      }
    }
  }

  /** How many ticks are currently held for `id` — the buffer depth a HUD or a test can read. */
  depth(id: string): number {
    return this.bookFor(id).size;
  }
}
